import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import minMax from 'dayjs/plugin/minMax';
import isoWeek from 'dayjs/plugin/isoWeek';
import weekday from 'dayjs/plugin/weekday';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isBetween from 'dayjs/plugin/isBetween';
import objectSupport from 'dayjs/plugin/objectSupport';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { last } from 'lodash';

dayjs.extend(utc);
dayjs.extend(minMax);
dayjs.extend(isoWeek);
dayjs.extend(weekday);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);
dayjs.extend(objectSupport);
dayjs.extend(customParseFormat);

export function useDayjs() {
  const getMinDate = (dates, format) => {
    const minDate = dayjs.min(dates);
    if (!format) {
      return minDate;
    }
    return minDate.format(format);
  };

  const getMaxDate = (dates, format) => {
    const maxDate = dayjs.max(dates);
    if (!format) {
      return maxDate;
    }
    return maxDate.format(format);
  };

  const sortDates = (dates, direction = 'asc') => {
    const isDirectionValid = ['asc', 'desc'].includes(direction);
    if (!isDirectionValid) {
      throw new Error('Could not sort dates. direction must be "asc" or "desc".');
    }
    const descending = direction === 'desc';

    return dates.sort((a, b) => {
      const diff = dayjs.utc(a).valueOf() - dayjs.utc(b).valueOf();
      return descending ? -diff : diff;
    });
  };

  const formatDate = (date, format) => {
    if (!date) return null;
    return dayjs(date).format(format);
  };

  const formatUtcDate = (date, format) => {
    const utcDate = dayjs.utc(date);
    if (!format) {
      return utcDate;
    }
    return utcDate.format(format);
  };

  const getIsoWeekday = date => {
    return dayjs(date).isoWeekday();
  };

  const checkDateIsSame = (date, refDate, unitOfMeasurement) => {
    return dayjs(date).isSame(refDate, unitOfMeasurement);
  };

  const checkDateIsSameOrBefore = (date, refDate, unitOfMeasurement) => {
    return dayjs(date).isSameOrBefore(refDate, unitOfMeasurement);
  };

  const checkDateIsBefore = (date, refDate, unitOfMeasurement) => {
    return dayjs(date).isBefore(refDate, unitOfMeasurement);
  };

  const checkDateIsSameOrAfter = (date, refDate, unitOfMeasurement) => {
    return dayjs(date).isSameOrAfter(refDate, unitOfMeasurement);
  };

  const checkDateIsAfter = (date, refDate, unitOfMeasurement) => {
    return dayjs(date).isAfter(refDate, unitOfMeasurement);
  };

  const setDefaultDateTime = date => {
    return dayjs(date)
      .set('hour', 0)
      .set('minute', 0)
      .set('second', 0)
      .format('MM/DD/YYYY HH:mm:ss');
  };

  const getDatesDiff = (date, refDate, unitOfMeasurement, float = false) => {
    return dayjs(date).diff(refDate, unitOfMeasurement, float);
  };

  const calculateEndDate = ({ startDate, weeks, dateFormat }) => {
    if (!startDate || !weeks) return null; // dayjs returns current date / time when passed undefined, so exit early.
    if (weeks > 9999999) return null; // Will produce valid dates (to dayjs) but invalid dates (to us)
    return dayjs
      .utc(startDate)
      .add(weeks - 1, 'weeks') // go forward one week less to account for shift forward to sunday
      .endOf('isoWeek') // select Sunday
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 }) // end of isoWeek is sunday T23:59:59.999Z
      .format(dateFormat); // should return same format as picker since it's run through formatDateForSave
  };

  const calculateStartDate = ({ endDate, weeks, dateFormat }) => {
    if (!endDate || !weeks) return null; // dayjs returns current date / time when passed undefined, so exit early.

    return dayjs
      .utc(endDate)
      .subtract(weeks - 1, 'weeks') // go back one week less to account for shift back to monday
      .startOf('isoWeek') // select Monday
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      .format(dateFormat);
  };

  const convertLocalDatetimeToUtcDate = date => {
    const localDatetime = dayjs(date);
    return localDatetime.utc(true).format();
  };

  const toNearestSunday = date => {
    // Returns Sunday before date. if date is Sunday, return that Sunday.
    let parsedDate = dayjs.utc(date);
    // if latestSnapshotDate is not Sunday, go back 1 week.
    if (parsedDate.isoWeekday() !== 7) {
      parsedDate = parsedDate.subtract(1, 'week');
    }
    return parsedDate.endOf('isoWeek').set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
  };

  const formatDateForPicker = (date, format) => {
    if (!date) return null;

    return dayjs.utc(date).format(format);
  };

  const formatDateForSave = (date, format) => {
    if (!date) return null;

    // save date in specified format, with 0 utcOffset, 0 for time. e.g. 2018-01-01T00:00:00.000Z
    return dayjs
      .utc(date, format)
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      .toISOString();
  };

  const createDateForSave = () => {
    // save date in specified format, with 0 utcOffset, 0 for time. e.g. 2018-01-01T00:00:00.000Z
    return dayjs.utc().set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toISOString();
  };

  /**
   * Formats an array of dates to be used by the vuetify date picker component
   *
   * @param {Date[]} dates The dates to be formatted
   * @param {string} format The date picker format
   * @returns {string[]} The formatted Dates
   */
  const formatDatesForPicker = (dates, format) => {
    if (!dates) return null;
    return dates.map(date => formatDateForPicker(date, format));
  };

  const getUTCFromDate = date => {
    if (!date) return null;

    return dayjs.utc(date);
  };

  const isDateValid = ({ dateValue, dateFormat, allowNull = true }) => {
    if (allowNull && !dateValue) return true;
    return dayjs.utc(dateValue, dateFormat, true).isValid();
  };

  function goToDay(date, direction, targetDay) {
    if (!date) return null;
    date = dayjs(date);

    const WEEKDAYS = {
      sunday: 0,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
    };

    const currentDay = date.day();
    const targetDayNum = WEEKDAYS[targetDay.toLowerCase()];

    // If we're already on the target day, return the current date
    if (currentDay === targetDayNum) {
      return date;
    }

    if (direction.toLowerCase() === 'next') {
      // Calculate days until next occurrence
      const daysToAdd = (targetDayNum - currentDay + 7) % 7;
      return date.add(daysToAdd, 'day');
    }

    // Calculate days until previous occurrence
    const daysToSubtract = (currentDay - targetDayNum + 7) % 7;
    return date.subtract(daysToSubtract, 'day');
  }

  const getUTCNow = () => dayjs.utc();

  function zeroOutTzOffset(date) {
    // We need native Dates. However, these convert to user TZ automatically.
    // To preserve the time, we convert to dayjs then move ourselves manually back to where we were.
    // This produces a Date object that matches our original time.

    if (last(date) === 'Z') {
      // Z denotes 0 offset. That breaks the processing below. If it exists, strip it.
      date = date.slice(0, -1);
    }

    const currentTzOffset = dayjs().utcOffset();
    date = dayjs(date);

    const methodToApply = currentTzOffset > 0 ? 'add' : 'subtract';

    return date[methodToApply](currentTzOffset, 'minutes');
  }

  function getTzZeroedDate(dateString) {
    const offsetAdjustedDate = zeroOutTzOffset(dateString);
    return new Date(offsetAdjustedDate);
  }

  return {
    dayjs,
    getMinDate,
    getMaxDate,
    sortDates,
    formatDate,
    formatUtcDate,
    getIsoWeekday,
    checkDateIsSameOrBefore,
    checkDateIsSameOrAfter,
    checkDateIsAfter,
    checkDateIsBefore,
    checkDateIsSame,
    convertLocalDatetimeToUtcDate,
    setDefaultDateTime,
    getDatesDiff,
    calculateEndDate,
    calculateStartDate,
    toNearestSunday,
    formatDateForPicker,
    formatDateForSave,
    createDateForSave,
    formatDatesForPicker,
    getUTCFromDate,
    isDateValid,
    getUTCNow,
    goToDay,
    getTzZeroedDate,
    zeroOutTzOffset,
  };
}
